import Big, {BigSource} from 'big.js';
import {combineLatest, tap} from 'rxjs';
import {map, distinctUntilChanged, share, throttleTime} from 'rxjs/operators';
import {contractUtil} from '@/helper/contract';
import {div, max, min, minus, numUtil, plus, times} from '@/helper/num';
import {qtyUtil} from '@/helper/qty-type-contract';
import {calcMaintenanceMargin, LeverageDefault, leverageUtil} from '@/helper/leverage';
import {DEFAULT_MONEY_PRECISION} from '@/const/data-config';
import {calcUnrealisedPnlEv, calcValueRv} from '@/calc';
import {
  contractsCache$,
  leverageMarginsCache$,
  currenciesCurrencyCache$,
  riskEngineMode$,
  contractDefaultTakerFeeRateEr$,
  coinMarginedContractGray$,
} from '@/config';
import {fiat$} from '@/state/fiat';
import {indexPricesCache$} from '@/price-index/price-index';
import {contractAccountsCache$} from '@/contract-accounts/contract-accounts-cache';
import {contractMarketRealtimeCache$} from '@/market-contract/market24h-contract-cache';
import {positionsCache$} from './positions-cache';
import {riskUnitsCache$} from '@/ras/ras';
import type {
  ws,
  PriceEntity,
  FiatEntity,
  ContractPair,
  ContractMarket,
  ContractAccount,
  Position,
  LeverageMargin,
} from '@/types';
import type {RiskUnit} from '@/types/ws';

type ContractPositionRaw = ws.ContractPositionRaw;

const fiatIndexPriceEntity$ = combineLatest([fiat$, indexPricesCache$]).pipe(
  map(([fiat, indexPricesCache]) => indexPricesCache.get('.' + fiat)),
  distinctUntilChanged()
);
export const fiatEntity$ = combineLatest([fiat$, fiatIndexPriceEntity$]).pipe(
  map(([fiat, fiatIndexPriceEntity]) => convertFiatEntity(fiat, fiatIndexPriceEntity))
);

export const marginRatioCache$ = combineLatest([
  riskUnitsCache$,
  positionsCache$,
  contractAccountsCache$,
  leverageMarginsCache$,
  contractsCache$,
  contractMarketRealtimeCache$,
  currenciesCurrencyCache$,
  contractDefaultTakerFeeRateEr$,
]).pipe(
  throttleTime(500),
  map(
    ([
      riskUnitsCache,
      positionsCache,
      contractAccountsCache,
      marginCache,
      contractsCache,
      contractMarketCache,
      currenciesCache,
      defaultTakerFeeRateEr,
    ]) => {
      const riskUnits = riskUnitsCache.toArray<RiskUnit>();
      const positions = positionsCache.toArray<ContractPositionRaw>();
      const marginRatioCache = riskUnits
        .filter(({valuationCurrency}) => ['USDT', 'USDC', 'MUSDT'].includes(valuationCurrency))
        .map(({symbol, posSide, valuationCurrency, ...riskUnit}) => {
          const {valueScale, valuePrecision} = currenciesCache.get(valuationCurrency);
          const scale = ['USDC', 'USDT', 'MUSDT', 'PT'].indexOf(valuationCurrency) > -1 ? 0 : valueScale;
          if (!symbol) {
            const crossPositions = positions.filter(({isCross, size, type, currency}) => {
              return isCross && size > 0 && type === 'PerpetualV2' && valuationCurrency === currency;
            });

            const allCrossOneWayMM = crossPositions
              .filter(({posMode}) => posMode === 'OneWay')
              .map((position) => {
                const {mmCost} = calcNormalPosMMCost(
                  position,
                  marginCache,
                  contractsCache,
                  contractMarketCache,
                  defaultTakerFeeRateEr
                );
                return mmCost;
              })
              .reduce((p, c) => p.plus(c), Big(0));

            const hedgedPositions = crossPositions.filter(({posMode}) => posMode === 'Hedged');
            const hedgedSymbols = Array.from(new Set(hedgedPositions.map(({symbol}) => symbol)));
            const allCrossHedgedMM = hedgedSymbols
              .map((symbol) => {
                const curPositions = hedgedPositions.filter((item) => item.symbol === symbol);
                const posLong = curPositions.find(({posSide}) => posSide === 'Long');
                const posShort = curPositions.find(({posSide}) => posSide === 'Short');
                const {mmCost} = calcHedgedPosMM(
                  posLong,
                  posShort,
                  marginCache,
                  contractsCache,
                  contractMarketCache,
                  defaultTakerFeeRateEr
                );
                return mmCost;
              })
              .reduce((p, c) => p.plus(c), Big(0));

            const allCrossUnPnlRv = crossPositions.reduce((p, c) => {
              const market = contractMarketCache.get(c.symbol);
              const contract = contractsCache.get(c.symbol);
              const markPriceEp = market ? market.markPriceEp : 0;
              const unrealisedPnlEv = appendUnrealisedPnl(c, markPriceEp, contract);
              return p.plus(unrealisedPnlEv);
            }, Big(0));
            const {accountBalanceRv, totalOrdUsedBalanceRv, fixedUsedRv} = riskUnit;
            const totalEquityRv = Big(accountBalanceRv)
              .plus(allCrossUnPnlRv)
              .minus(fixedUsedRv || 0)
              .minus(totalOrdUsedBalanceRv || 0);
            const maintenanceMarginRv = allCrossOneWayMM.plus(allCrossHedgedMM);
            const totalEquityRv2 = totalEquityRv.gt(0) ? totalEquityRv : Big(0);
            const maintenanceMarginRv2 = maintenanceMarginRv.gt(0) ? maintenanceMarginRv : Big(0);

            const {marginRatioRr, marginRatio} = calcMarginRatio(totalEquityRv, maintenanceMarginRv);
            return {
              key: valuationCurrency + 'Cross',
              symbol: '',
              side: '',
              currency: valuationCurrency,
              totalEquity: numUtil.floor(totalEquityRv2, valuePrecision, scale),
              maintenanceMargin: numUtil.floor(maintenanceMarginRv2, valuePrecision, scale),
              marginRatioRr,
              marginRatio,
            };
          } else {
            const riskKey = symbol + posSide;
            if (positionsCache.has(riskKey)) {
              const position = positionsCache.get(riskKey);
              const contract = contractsCache.get(position.symbol);
              const {mmCost} = calcNormalPosMMCost(
                position,
                marginCache,
                contractsCache,
                contractMarketCache,
                defaultTakerFeeRateEr
              );
              const {accountBalanceRv} = riskUnit;
              const market = contractMarketCache.get(position.symbol);
              const markPriceEp = market ? market.markPriceEp : 0;
              const unrealisedPnlRv = appendUnrealisedPnl(position, markPriceEp, contract);
              const totalEquityRv = Big(accountBalanceRv).plus(unrealisedPnlRv);
              const totalEquityRv2 = totalEquityRv.gt(0) ? totalEquityRv : Big(0);
              const maintenanceMarginRv2 = Big(mmCost).gt(0) ? mmCost : Big(0);
              const {marginRatioRr, marginRatio} = calcMarginRatio(totalEquityRv, mmCost);

              return {
                key: riskKey,
                symbol,
                side: position.side,
                currency: valuationCurrency,
                totalEquity: numUtil.floor(totalEquityRv2, valuePrecision, scale),
                maintenanceMargin: numUtil.floor(maintenanceMarginRv2, valuePrecision, scale),
                marginRatioRr: Number(marginRatioRr),
                marginRatio,
              };
            }
            return null;
          }
        })
        .filter(Boolean)
        .reduce((res, cur) => res.set(cur.key, cur), new Map());

      return positions
        .filter(({type}) => type === 'Perpetual')
        .map(({symbol, posSide, currency, isCross}) => {
          if (!contractsCache.has(symbol) || !currenciesCache.has(currency)) {
            return null;
          }
          const {contractSide, settleCurrency} = contractsCache.get(symbol);
          if (contractSide > 0) {
            return null;
          }
          const {valuePrecision, valueFactor} = currenciesCache.get(currency);
          if (positionsCache.has(symbol)) {
            const position = positionsCache.get(symbol);
            const contract = contractsCache.get(position.symbol);
            const {mmCost} = calcNormalPosMMCost(
              position,
              marginCache,
              contractsCache,
              contractMarketCache,
              defaultTakerFeeRateEr
            );
            const {accountBalanceEv} = contractAccountsCache.get(settleCurrency) || {
              accountBalanceEv: 0,
            };
            const market = contractMarketCache.get(position.symbol);
            const markPriceEp = market ? market.markPriceEp : 0;
            const unrealisedPnlEv = appendUnrealisedPnl(position, markPriceEp, contract);

            const totalEquityRv = isCross
              ? Big(accountBalanceEv)
                  .plus(unrealisedPnlEv)
                  .minus(position.orderCostEv || 0)
                  .minus(position.estimatedOrdLossEv || 0)
                  .div(valueFactor)
              : Big(position.assignedPosBalanceEv).plus(unrealisedPnlEv).div(valueFactor);
            const totalEquityRv2 = totalEquityRv.gt(0) ? totalEquityRv : Big(0);
            const maintenanceMarginRv2 = Big(mmCost).gt(0) ? mmCost : Big(0);
            const {marginRatioRr, marginRatio} = calcMarginRatio(totalEquityRv2, mmCost);

            return {
              key: symbol + (posSide || 'Merged'),
              symbol,
              side: position.side,
              currency,
              totalEquity: numUtil.floor(totalEquityRv2, valuePrecision, 0),
              maintenanceMargin: numUtil.floor(maintenanceMarginRv2, valuePrecision),
              marginRatioRr: Number(marginRatioRr),
              marginRatio,
            };
          }
          return null;
        })
        .filter(Boolean)
        .reduce((res, cur) => res.set(cur.key, cur), marginRatioCache);
    }
  )
);

export function calcAirdropMarginRatio(
  position,
  currenciesCache,
  marginCache,
  contractsCache,
  contractMarketCache,
  defaultTakerFeeRateEr
) {
  const {symbol, isCross, size, posMode, posSide} = position;
  if (size <= 0 || !contractsCache.has(symbol)) {
    return null;
  }
  const contract = contractsCache.get(position.symbol);
  const {contractSide, settleCurrency, type} = contract;
  if (contractSide < 0 || !currenciesCache.has(settleCurrency)) {
    return null;
  }
  const {valueScale, valuePrecision, valueFactor} = currenciesCache.get(settleCurrency);
  const scale = ['USDC', 'USDT', 'MUSDT', 'PT'].indexOf(settleCurrency) > -1 ? 0 : valueScale;
  const accountBalanceEv = position.assignedPosBalanceEv;
  const key = symbol + (posSide || 'Merged') + '_' + position.userID;
  if (type === 'PerpetualV2') {
    let mmCost: any = {mmCost: 0};
    if (isCross) {
      if (posMode === 'OneWay') {
        mmCost = calcNormalPosMMCost(position, marginCache, contractsCache, contractMarketCache, defaultTakerFeeRateEr);
      } else if (posMode === 'Hedged') {
        const posLong = posSide === 'Long' ? position : null;
        const posShort = posSide === 'Short' ? position : null;
        mmCost = calcHedgedPosMM(
          posLong,
          posShort,
          marginCache,
          contractsCache,
          contractMarketCache,
          defaultTakerFeeRateEr
        );
      }
      const market = contractMarketCache.get(symbol);
      const contract = contractsCache.get(symbol);
      const markPriceEp = market ? market.markPriceEp : 0;
      const crossUnPnlRv = appendUnrealisedPnl(position, markPriceEp, contract);
      const totalEquityRv = Big(accountBalanceEv).plus(crossUnPnlRv);
      const totalEquityRv2 = totalEquityRv.gt(0) ? totalEquityRv : Big(0);
      const maintenanceMarginRv = Big(mmCost.mmCost);
      const maintenanceMarginRv2 = maintenanceMarginRv.gt(0) ? maintenanceMarginRv : Big(0);
      const {marginRatioRr, marginRatio} = calcMarginRatio(totalEquityRv, maintenanceMarginRv);
      return {
        key,
        symbol,
        totalEquity: numUtil.floor(totalEquityRv2, valuePrecision, scale),
        maintenanceMargin: numUtil.floor(maintenanceMarginRv2, valuePrecision, scale),
        marginRatioRr,
        marginRatio,
      };
    } else {
      const contract = contractsCache.get(position.symbol);
      const {mmCost} = calcNormalPosMMCost(
        position,
        marginCache,
        contractsCache,
        contractMarketCache,
        defaultTakerFeeRateEr
      );
      const market = contractMarketCache.get(position.symbol);
      const markPriceEp = market ? market.markPriceEp : 0;
      const unrealisedPnlRv = appendUnrealisedPnl(position, markPriceEp, contract);
      const totalEquityRv = Big(accountBalanceEv).plus(unrealisedPnlRv);
      const totalEquityRv2 = totalEquityRv.gt(0) ? totalEquityRv : Big(0);
      const maintenanceMarginRv2 = Big(mmCost).gt(0) ? mmCost : Big(0);
      const {marginRatioRr, marginRatio} = calcMarginRatio(totalEquityRv, mmCost);

      return {
        key,
        symbol,
        side: position.side,
        totalEquity: numUtil.floor(totalEquityRv2, valuePrecision, scale),
        maintenanceMargin: numUtil.floor(maintenanceMarginRv2, valuePrecision, scale),
        marginRatioRr: Number(marginRatioRr),
        marginRatio,
      };
    }
  } else if (type === 'Perpetual') {
    const {mmCost} = calcNormalPosMMCost(
      position,
      marginCache,
      contractsCache,
      contractMarketCache,
      defaultTakerFeeRateEr
    );
    const market = contractMarketCache.get(position.symbol);
    const markPriceEp = market ? market.markPriceEp : 0;
    const unrealisedPnlEv = appendUnrealisedPnl(position, markPriceEp, contract);

    const totalEquityRv = isCross
      ? Big(accountBalanceEv)
          .plus(unrealisedPnlEv)
          .minus(position.orderCostEv || 0)
          .minus(position.estimatedOrdLossEv || 0)
          .div(valueFactor)
      : Big(position.assignedPosBalanceEv).plus(unrealisedPnlEv).div(valueFactor);
    const totalEquityRv2 = totalEquityRv.gt(0) ? totalEquityRv : Big(0);
    const maintenanceMarginRv2 = Big(mmCost).gt(0) ? mmCost : Big(0);
    const {marginRatioRr, marginRatio} = calcMarginRatio(totalEquityRv2, mmCost);

    return {
      key,
      symbol,
      totalEquity: numUtil.floor(totalEquityRv2, valuePrecision, 0),
      maintenanceMargin: numUtil.floor(maintenanceMarginRv2, valuePrecision),
      marginRatioRr: Number(marginRatioRr),
      marginRatio,
    };
  }
  return null;
}

export const positions$ = combineLatest([
  contractsCache$,
  positionsCache$,
  contractMarketRealtimeCache$,
  contractAccountsCache$,
  fiatEntity$,
  marginRatioCache$,
  contractDefaultTakerFeeRateEr$,
  riskEngineMode$,
  coinMarginedContractGray$,
]).pipe(
  map(
    ([
      contractsCache,
      positionsCache,
      contractMarket24hCache,
      contractAccountsCache,
      fiatEntity,
      marginRatioCache,
      defaultTakerFeeRateEr,
      riskEngineMode,
      coinMarginedContractGray,
    ]: any[]) =>
      convertPositions(
        contractsCache,
        positionsCache,
        contractMarket24hCache,
        contractAccountsCache,
        fiatEntity,
        marginRatioCache,
        defaultTakerFeeRateEr,
        riskEngineMode,
        coinMarginedContractGray
      )
  ),
  share()
);

function convertFiatEntity(fiat: string, fiatIndexPriceEntity: PriceEntity): FiatEntity {
  if (fiatIndexPriceEntity) {
    const {price, priceEp} = fiatIndexPriceEntity;
    return {fiat, price, priceEp};
  }
  return null;
}

export function convertPositions(
  contracts: Map<string, ContractPair>,
  positionsCache: Map<string, ContractPositionRaw>,
  contractMarket24hCache: Map<string, ContractMarket>,
  contractAccountsCache: Map<string, ContractAccount>,
  fiatEntity: FiatEntity,
  marginRatioCache,
  defaultTakerFeeRateEr,
  riskMode,
  coinMarginedContractGray,
  isAirdrop = false
): Position[] {
  return positionsCache
    .toArray<ContractPositionRaw>()
    .map((position) => {
      const {symbol, currency} = position;
      const contract = contracts.get(symbol);
      if (!contract) {
        return null;
      }
      const market = contractMarket24hCache.get(symbol);
      if (!market) {
        return null;
      }
      const account = contractAccountsCache.get(currency);
      if (!account && !position.isAirdropPos) {
        return null;
      }
      return convertPosition(
        position,
        contract,
        market,
        account,
        fiatEntity,
        marginRatioCache,
        defaultTakerFeeRateEr,
        riskMode,
        coinMarginedContractGray,
        isAirdrop
      );
    })
    .filter(Boolean);
}

function convertPosition(
  position: ContractPositionRaw,
  contract: ContractPair,
  market24h: ContractMarket,
  account: ContractAccount,
  fiatEntity: FiatEntity,
  marginRatioCache,
  defaultTakerFeeRateEr,
  riskMode: boolean,
  coinMarginedContractGray: boolean,
  isAirdrop = false
): Position {
  const {
    type,
    perpProductSubType,
    displaySymbol,
    settleCurrency,
    settleCurrencyDisplay,
    valuePrecision,
    pricePrecision,
    priceScale,
    valueScale,
  } = contract;
  const {
    valueEv,
    assignedPosBalanceEv,
    curTermRealisedPnlEv,
    curTermDiscountFeeEv,
    size,
    side,
    posMode,
    posSide,
    avgEntryPriceEp,
    liquidationPriceEp,
    riskLimitEv,
    buyLeavesValueEv,
    sellLeavesValueEv,
    isCross,
    symbol,
    bankruptCommEv, // 破产关仓手续费
    positionMarginEv, // 已经包含未接盈亏
  } = position;
  const defaultTakerFeeRateEr2 = contractUtil.getDefaultTakerFeeRate(contract, defaultTakerFeeRateEr);
  const marginEv = assignedPosBalanceEv || 0;
  const marginPrecision = contract.contractSide > 0 ? valuePrecision : 8;
  const margin = numUtil.floor(marginEv, marginPrecision, valueScale);
  const isPerpetualV2 = contract.type === 'PerpetualV2';
  let marginEv2 = marginEv;
  if ((riskMode && isPerpetualV2) || (coinMarginedContractGray && !isPerpetualV2)) {
    marginEv2 = Number(Big(positionMarginEv).plus(bankruptCommEv));
  }
  const margin2 = numUtil.floor(marginEv2, marginPrecision, valueScale);
  const lastPriceEp = market24h ? market24h.closeEp || 0 : 0;
  const markPriceEp = market24h ? market24h.markPriceEp || 0 : 0;
  const indexPriceEp = market24h ? market24h.indexPriceEp || 0 : 0;
  const realisedPnlEv = Number(minus(curTermRealisedPnlEv, curTermDiscountFeeEv || 0));
  const realisedPnl = numUtil.floor(realisedPnlEv, valuePrecision, valueScale);
  const unrealisedPnlEvByLastPrice = appendUnrealisedPnl(position, lastPriceEp, contract);
  const unrealisedPnlByLastPrice = numUtil.floor(unrealisedPnlEvByLastPrice, valuePrecision, valueScale);
  const unrealisedPnlEvByMarkPrice = appendUnrealisedPnl(position, markPriceEp, contract);

  const markPriceEp2 = market24h
    ? position.side === 'Buy'
      ? min(markPriceEp, market24h.bidEp || 0)
      : max(markPriceEp, market24h.askEp || 0)
    : 0;
  const unrealisedPnlEvByMarkPrice2 =
    contract.contractSide > 0 ? unrealisedPnlEvByMarkPrice : appendUnrealisedPnl(position, markPriceEp2, contract);

  const unrealisedPnlByMarkPrice = numUtil.floor(unrealisedPnlEvByMarkPrice, valuePrecision, valueScale);
  const maxRemovableEv = calcMaxRemovableEv(position, unrealisedPnlEvByMarkPrice);
  const maxRemovable = numUtil.floor(maxRemovableEv, valuePrecision, valueScale);
  const marginFiat = calcFiat(contract, fiatEntity, marginEv2, indexPriceEp);
  const realisedPnlFiat = calcFiat(contract, fiatEntity, realisedPnlEv, indexPriceEp);
  const unrealisedPnlByLastPriceFiat = calcFiat(contract, fiatEntity, unrealisedPnlEvByLastPrice, indexPriceEp);
  const unrealisedPnlByMarkPriceFiat = calcFiat(contract, fiatEntity, unrealisedPnlEvByMarkPrice, indexPriceEp);

  const leverageEr = calcLeverageEr(position, contract.ratioScale);
  const leverage = leverageUtil.leverageErToLeverage(leverageEr, contract.ratioScale, {removeEnd0: true, unit: ''});
  const {accountBalanceEv, totalUsedBalanceEv} = position.isAirdropPos
    ? {accountBalanceEv: marginEv, totalUsedBalanceEv: marginEv}
    : account;
  const availableBalanceEv = minus(accountBalanceEv, totalUsedBalanceEv);
  const actualMarginEv = plus(
    isPerpetualV2 ? plus(assignedPosBalanceEv, unrealisedPnlEvByMarkPrice) : assignedPosBalanceEv,
    max(availableBalanceEv, 0)
  );
  const posVal = isPerpetualV2 ? times(markPriceEp, size) : valueEv;
  const leverage2 = +actualMarginEv > 0 && +posVal > 0 ? Big(posVal).div(actualMarginEv) : 0;
  const actualLeverage = Big(leverage2).gt(0.01) ? numUtil.floor(leverage2, 2) : '0.01';
  const avgEntryPrice = numUtil.floor(avgEntryPriceEp, pricePrecision, priceScale);
  const markPrice = numUtil.floor(markPriceEp, pricePrecision, priceScale);
  const sizeByCont = qtyUtil.toContQuantity(contract, size);
  const sizeByContWU = qtyUtil.toCont(contract, size);
  const sizeByValue = qtyUtil.toValueQuantity(contract, size, markPrice);
  const sizeByValueWU = qtyUtil.toValue(contract, size, markPrice);
  const sizeByCoin = qtyUtil.toCoinQuantity(contract, size, markPrice);
  const sizeByCoinWU = qtyUtil.toCoin(contract, size, markPrice);
  const sizeByEntryValueWU =
    contract.contractSide > 0
      ? qtyUtil.toValue(contract, size, avgEntryPrice)
      : qtyUtil.toCoin(contract, size, avgEntryPrice);

  const riskKey = symbol + (posSide || 'Merged');
  const marginKey =
    (contract.contractSide > 0 && isCross ? settleCurrency + 'Cross' : riskKey) +
    (isAirdrop ? '_' + position.userID : '');
  const marginRatioRr = (marginRatioCache && marginRatioCache.get(marginKey)?.marginRatioRr) || 0;
  const marginRatio = (marginRatioCache && marginRatioCache.get(marginKey)?.marginRatio) || '--';
  const breakevenPriceEv = calcBreakevenPriceEv(contract, position, defaultTakerFeeRateEr2);
  return {
    ...position,
    type,
    perpProductSubType,
    posMode: posMode || 'OneWay',
    posSide: posSide || 'Merged',
    displaySymbol,
    settleCurrency,
    sizeStr: calcSizeStr(size, side),
    value: numUtil.floor(valueEv, valuePrecision, valueScale),
    valueWU: numUtil.delimit(numUtil.floor(valueEv, valuePrecision, valueScale)) + ' ' + settleCurrencyDisplay,
    avgEntryPrice: numUtil.delimit(avgEntryPrice),
    liquidationPrice: numUtil.delimit(numUtil.floor(liquidationPriceEp, pricePrecision, priceScale)),
    liquidationDistance: numUtil.floor(Math.abs(liquidationPriceEp - markPriceEp), pricePrecision, priceScale),
    riskLimit: numUtil.floor(riskLimitEv, 0, valueScale),
    leverageEr,
    leverage,
    leverageX: `${leverage}${LeverageDefault.unit}`,

    marginEv,
    margin,
    marginEv2,
    marginWU: numUtil.delimit(margin2) + ' ' + settleCurrencyDisplay,
    marginFiat,
    marginFiatWU: formatFiatWU(contract, fiatEntity, marginFiat),

    markPriceEp,
    markPrice: market24h ? market24h.markPrice : (0).toFixed(pricePrecision),

    realisedPnlEv,
    realisedPnl,
    realisedPnlWU: realisedPnl + ' ' + settleCurrencyDisplay,
    realisedPnlFiat,
    realisedPnlFiatWU: formatFiatWU(contract, fiatEntity, realisedPnlFiat),

    buyLeavesValue: numUtil.floor(buyLeavesValueEv, valuePrecision, valueScale),
    sellLeavesValue: numUtil.floor(sellLeavesValueEv, valuePrecision, valueScale),
    usedLimit: genUsedLimit(position, valuePrecision, valueScale),

    unrealisedPnlEvByLastPrice,
    unrealisedPnlByLastPrice,
    unrealisedPnlByLastPriceWU: unrealisedPnlByLastPrice + ' ' + settleCurrencyDisplay,
    unrealisedPnlByLastPriceFiat,
    unrealisedPnlByLastPriceFiatWU: formatFiatWU(contract, fiatEntity, unrealisedPnlByLastPriceFiat),
    roiByLastPrice: calcRoi(unrealisedPnlEvByLastPrice, marginEv2),

    unrealisedPnlEvByMarkPrice,
    unrealisedPnlByMarkPrice,
    unrealisedPnlByMarkPriceWU: unrealisedPnlByMarkPrice + ' ' + settleCurrencyDisplay,
    unrealisedPnlByMarkPriceFiat,
    unrealisedPnlByMarkPriceFiatWU: formatFiatWU(contract, fiatEntity, unrealisedPnlByMarkPriceFiat),
    roiByMarkPrice: calcRoi(unrealisedPnlEvByMarkPrice, marginEv2),

    unrealisedPnlEvByMarkPrice2,

    maxRemovableEv,
    maxRemovable,
    maxRemovableWU: maxRemovable + ' ' + settleCurrencyDisplay,
    actualLeverage,
    sizeByCont,
    sizeByContWU,
    sizeByValue,
    sizeByValueWU,
    sizeByCoin,
    sizeByCoinWU,
    sizeByEntryValueWU,
    marginRatio,
    marginRatioRr,

    breakevenPriceEv,
    breakevenPrice: numUtil.delimit(numUtil.floor(breakevenPriceEv, pricePrecision, priceScale)),
    isAirdrop,
  };
}

function calcSizeStr(size: number, side: string) {
  if (size === 0) {
    return '0';
  }
  return (side === 'Sell' ? '-' : '') + numUtil.delimit(size);
}

function genUsedLimit(position, valuePrecision, scale) {
  if (!position) {
    return (0).toFixed(valuePrecision);
  }

  const buyPosOrdValueEv = position.buyLeavesValueEv + (position.side === 'Buy' ? position.valueEv : 0);
  const sellPosOrdValueEv = position.sellLeavesValueEv + (position.side === 'Sell' ? position.valueEv : 0);
  return numUtil.floor(Math.max(buyPosOrdValueEv, sellPosOrdValueEv), valuePrecision, scale);
}

function appendUnrealisedPnl(position, exitPriceEp, contract) {
  if (!contract) {
    return 0;
  }
  const {contractSide, contractSize, valueFactor, priceFactor} = contract;
  return calcUnrealisedPnlEv({
    side: position.side,
    contractSide,
    contractSize,
    qty: position.size,
    entryPriceEp: position.avgEntryPriceEp,
    exitPriceEp,
    factor: valueFactor,
    priceFactor,
  });
}

function calcRoi(unrealisedPnlEv, marginEv) {
  if (marginEv === 0) {
    return '0.00%';
  }
  return numUtil.floor(div(unrealisedPnlEv * 100, marginEv), 2) + '%';
}

function calcMaxRemovableEv(position: ContractPositionRaw, unrealisedPnlEvByMarkPrice) {
  const {assignedPosBalanceEv, posCostEv, size} = position;
  if (size === 0) {
    return 0;
  }
  const unrealisedPnlEv = unrealisedPnlEvByMarkPrice > 0 ? 0 : Math.abs(unrealisedPnlEvByMarkPrice);
  const removeMarginEv = Big(assignedPosBalanceEv).minus(posCostEv).minus(unrealisedPnlEv);
  return removeMarginEv.gt(0) ? Math.floor(Number(removeMarginEv)) : 0;
}

function calcFiat(contract: ContractPair, fiatEntity: FiatEntity, valueEv: number, indexPriceEp: number) {
  if (!fiatEntity) {
    return '';
  }
  const {contractSide, valueScale, isSupportHedge} = contract;
  //TODO 新币对value是真实值，而法币都是放大的值，故将value放大之后再进行计算
  const priceFactor = isSupportHedge ? 1e4 : 1;
  const fiatValueEv = Big(valueEv).times(priceFactor).div(fiatEntity.priceEp);
  if (contractSide > 0) {
    const fiatValue = numUtil.floor(fiatValueEv, DEFAULT_MONEY_PRECISION);
    return numUtil.delimit(fiatValue);
  }
  const fiatValue = numUtil.floor(Big(fiatValueEv).times(indexPriceEp), DEFAULT_MONEY_PRECISION, valueScale);
  return numUtil.delimit(fiatValue);
}

function formatFiatWU(contract: ContractPair, fiatEntity: FiatEntity, value) {
  if (!fiatEntity) {
    return '';
  }
  const {fiat} = fiatEntity;
  if (contract.settleCurrency === 'USD' && fiat === 'USD') {
    return '';
  }

  return value + ' ' + fiat;
}

function calcLeverageEr(position: ContractPositionRaw, ratioScale) {
  if (position.leverageEr === 0) {
    return leverageUtil.marginRateErToLeverageEr(position.initMarginReqEr, ratioScale, ratioScale);
  }
  return position.leverageEr;
}

function genMM(position, marginCache, contractsCache, markPriceEp) {
  if (!position) {
    return 0;
  }
  const {symbol} = position;
  const contract = contractsCache.get(symbol);
  const {maintenanceMargin} = calcMaintenanceMargin(contract, {...position, markPriceEp}, marginCache);
  return maintenanceMargin || 0;
}

function calcHedgedPosMM(
  posLong: ContractPositionRaw,
  posShort: ContractPositionRaw,
  marginCache: Map<string, LeverageMargin>,
  contractsCache: Map<string, ContractPair>,
  contractMarketCache: Map<string, ContractMarket>,
  defaultTakerFeeRateEr: number
) {
  if ((!posLong && !posShort) || (posLong?.size <= 0 && posShort?.size <= 0)) {
    return {
      mm: 0,
      posValue: 0,
      mmCost: 0,
    };
  }
  const longSize = posLong?.size || 0;
  const shortSize = posShort?.size || 0;

  const morePosition = longSize > shortSize ? posLong : posShort;
  const lessPosition = longSize > shortSize ? posShort : posLong;

  const markPriceEp = getMarkPriceEp(morePosition?.symbol, contractMarketCache);
  const morePosMM = morePosition ? genMM(morePosition, marginCache, contractsCache, markPriceEp) : 0;
  const lessPosMM = lessPosition ? genMM(lessPosition, marginCache, contractsCache, markPriceEp) : 0;

  const mm = Big(morePosMM).minus(lessPosMM);
  const posValue = minus(times(morePosition?.size || 0, markPriceEp), times(lessPosition?.size || 0, markPriceEp));
  const contract = contractsCache.get(posLong?.symbol || posShort?.symbol);

  const closePosFee = calcClosePosDefaultFee(
    contract.contractSide,
    morePosition.posSide === 'Long',
    posValue,
    mm,
    defaultTakerFeeRateEr
  );

  return {
    mm,
    posValue,
    mmCost: plus(closePosFee, mm),
  };
}

function calcNormalPosMMCost(
  position: ContractPositionRaw,
  marginCache: Map<string, LeverageMargin>,
  contractsCache: Map<string, ContractPair>,
  contractMarketCache: Map<string, ContractMarket>,
  defaultTakerFeeRateEr: number
) {
  if (!position || position.size <= 0) {
    return {
      mm: 0,
      posValue: 0,
      mmCost: 0,
    };
  }

  const markPriceEp = getMarkPriceEp(position.symbol, contractMarketCache);
  const mm = genMM(position, marginCache, contractsCache, markPriceEp);
  const contract = contractsCache.get(position.symbol);
  const posValue = calcValueRv(
    contract.contractSide,
    contract.contractSize,
    contract.priceFactor,
    position.size,
    markPriceEp
  );
  const closePosFee = calcClosePosDefaultFee(
    contract.contractSide,
    position.side === 'Buy',
    posValue,
    mm,
    contractUtil.getDefaultTakerFeeRate(contract, defaultTakerFeeRateEr)
  );
  return {
    mm,
    posValue,
    mmCost: plus(closePosFee, mm),
  };
}

function calcMarginRatio(totalEquityRv: Big, mmCost: BigSource) {
  const rawMR = totalEquityRv.gt(0) && Big(mmCost).gt(0) ? totalEquityRv.div(mmCost) : Big(999);
  const marginRatioRr = rawMR.gt(999) ? 999 : Number(rawMR);
  const marginRatio = numUtil.floor(marginRatioRr, 2, -2) + '%';
  return {marginRatioRr, marginRatio};
}

function calcClosePosDefaultFee(contractSide, isLong, posValue, maintenanceMarginRv, defaultTakerFeeRate) {
  if ((contractSide > 0 && isLong) || (contractSide < 0 && !isLong)) {
    return Big(posValue).minus(maintenanceMarginRv).times(defaultTakerFeeRate);
  }
  return Big(posValue).plus(maintenanceMarginRv).times(defaultTakerFeeRate);
}

function getMarkPriceEp(symbol, marketCache) {
  const market = marketCache.get(symbol);
  return market ? market.markPriceEp : 0;
}

function calcBreakevenPriceEv(contract: ContractPair, position: ContractPositionRaw, defaultTakerFeeRateEr) {
  // long: ( 盈亏平衡价 - 入场价 ) * 数量 = 开仓手续费 + 平仓手续费
  // long: breakevenPrice = entryPrice * (1 + position.takerFeeRate) / (1 - defaultTakerFeeRateEr)
  // short: ( 入场价 - 盈亏平衡价 ) * 数量 = 开仓手续费 + 平仓手续费
  // short: breakevenPrice = entryPrice * (1 - position.takerFeeRate) / (1 + defaultTakerFeeRateEr)

  if (position.size > 0) {
    const {avgEntryPriceEp, takerFeeRateEr} = position;
    // 如果takerFee为-1，代表默认，使用默认值；先hardcode，后续pm增加该默认值，或者datagw做兼容
    const takerFeeRateEr2 = takerFeeRateEr > 0 ? takerFeeRateEr / contract.ratioFactor : defaultTakerFeeRateEr;
    if (position.side === 'Buy') {
      return Big(avgEntryPriceEp)
        .times(Big(1).plus(takerFeeRateEr2))
        .div(Big(1).minus(defaultTakerFeeRateEr))
        .toNumber();
    }
    if (position.side === 'Sell') {
      return Big(avgEntryPriceEp)
        .times(Big(1).minus(takerFeeRateEr2))
        .div(Big(1).plus(defaultTakerFeeRateEr))
        .toNumber();
    }
  }
  return 0;
}
